import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';
import { useAuth } from '../context/AuthContext';
import { joinUrl } from '../utils/meet';

// Toplantılar → Takvim: operations@boldranks.com takvimi (Google) + CRM'de planlananlar + Fireflies kayıtları.
// Hafta görünümü (dar ekranda gün görünümü). İptal edilen etkinlikler gösterilmez.

const HOUR_PX = 52;
const DAY_MS = 86400000;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const startOfWeek = (d) => { const x = startOfDay(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); return x; };
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const hm = (d) => d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

function endOf(m) {
  const s = new Date(m.meeting_date);
  if (m.end_at) { const e = new Date(m.end_at); if (e > s) return e; }
  return new Date(s.getTime() + (m.duration_minutes || 30) * 60000);
}

// Katıl butonu: başlamadan 15 dk önce → bitene kadar öne çıkar
export function joinState(m, now = new Date()) {
  if (!m.meet_link || m.calendar_status === 'cancelled' || !m.meeting_date) return null;
  const s = new Date(m.meeting_date); const e = endOf(m);
  if (now >= new Date(s.getTime() - 15 * 60000) && now <= e) return 'now';
  return now < s ? 'later' : 'past';
}

// Aynı gün çakışan toplantıları yan yana sütunlara böl
function layoutDay(items) {
  const sorted = [...items].sort((a, b) => a.s - b.s || b.e - a.e);
  const out = []; let cluster = []; let clusterEnd = 0;
  const flush = () => {
    const cols = [];
    for (const it of cluster) {
      let c = cols.findIndex(end => end <= it.s);
      if (c === -1) { c = cols.length; cols.push(0); }
      cols[c] = it.e; it.col = c;
    }
    cluster.forEach(it => { it.cols = cols.length; out.push(it); });
    cluster = []; clusterEnd = 0;
  };
  for (const it of sorted) {
    if (cluster.length && it.s >= clusterEnd) flush();
    cluster.push(it); clusterEnd = Math.max(clusterEnd, it.e);
  }
  if (cluster.length) flush();
  return out;
}

function MeetingDetail({ m, users = [], onClose, onPlay }) {
  const { user } = useAuth();
  const nameOf = (e) => users.find(u => (u.email || '').toLowerCase() === String(e).toLowerCase())?.name;
  const s = new Date(m.meeting_date); const e = endOf(m);
  const people = Array.isArray(m.participant_emails) && m.participant_emails.length
    ? m.participant_emails : String(m.attendees || '').split(',').map(x => x.trim()).filter(Boolean);
  const join = joinState(m);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal mcal-detail" onClick={(ev) => ev.stopPropagation()}>
        <div className="mcal-detail__head">
          <h2 className="modal__title">{m.title}</h2>
          <button type="button" className="sc-remove" onClick={onClose} aria-label="Kapat"><Icon name="x" size={14} /></button>
        </div>
        <p className="mcal-detail__when">
          {s.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })} · {hm(s)} – {hm(e)}
        </p>
        {m.account_company && <p className="mcal-detail__brand">{m.account_company}{m.account_status === 'internal' ? ' (iç grup)' : ''}</p>}
        <div className="mcal-detail__actions">
          {m.meet_link && (
            <a className={`btn ${join === 'now' ? 'btn--primary' : 'btn--secondary'}`} href={joinUrl(m.meet_link, user?.email)} target="_blank" rel="noopener noreferrer">
              <Icon name="video" size={14} /> Google Meet ile katıl
            </a>
          )}
          {m.fireflies_id && (
            <button type="button" className="btn btn--secondary" onClick={() => onPlay(m)}><Icon name="play" size={12} /> Kaydı izle</button>
          )}
          {m.calendar_html_link && (
            <a className="btn btn--secondary" href={m.calendar_html_link} target="_blank" rel="noopener noreferrer">Google Takvim'de aç</a>
          )}
        </div>
        {m.meet_link && <p className="mcal-detail__link">{m.meet_link.replace(/^https?:\/\//, '').replace(/\?.*$/, '')}</p>}
        {people.length > 0 && (
          <div className="mcal-detail__people">
            <div className="form-label">Davetliler ({people.length})</div>
            <ul>{people.slice(0, 30).map(p => <li key={p}>{nameOf(p) ? <>{nameOf(p)} <span className="text-muted">· {p}</span></> : p}</li>)}</ul>
          </div>
        )}
        {(m.notes || m.outcome) && <p className="mcal-detail__note">{m.outcome || m.notes}</p>}
      </div>
    </div>
  );
}

export default function MeetingCalendar({ meetings, users, onPlay }) {
  const { user } = useAuth();
  const narrow = typeof window !== 'undefined' && window.innerWidth < 768;
  const [view, setView] = useState(narrow ? 'day' : 'week');
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [open, setOpen] = useState(null);
  const [now, setNow] = useState(new Date());
  const gridRef = useRef(null);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);

  const from = view === 'week' ? startOfWeek(anchor) : startOfDay(anchor);
  const days = Array.from({ length: view === 'week' ? 7 : 1 }, (_, i) => new Date(from.getTime() + i * DAY_MS));
  const to = new Date(from.getTime() + days.length * DAY_MS);

  const items = useMemo(() => meetings
    .filter(m => m.meeting_date && m.calendar_status !== 'cancelled')
    .map(m => ({ m, s: new Date(m.meeting_date), e: endOf(m) }))
    .filter(x => x.s < to && x.e > from), [meetings, from.getTime(), to.getTime()]); // eslint-disable-line react-hooks/exhaustive-deps

  // Görünen saat aralığı: 08–20, daha erken/geç toplantı varsa genişler
  let minH = 8; let maxH = 20;
  for (const x of items) { minH = Math.min(minH, x.s.getHours()); maxH = Math.max(maxH, Math.min(24, x.e.getHours() + (x.e.getMinutes() ? 1 : 0))); }
  const hours = Array.from({ length: maxH - minH }, (_, i) => minH + i);

  const step = (dir) => setAnchor(new Date(anchor.getTime() + dir * (view === 'week' ? 7 : 1) * DAY_MS));
  const label = view === 'week'
    ? `${days[0].toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })} – ${days[6].toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}`
    : days[0].toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const topOf = (d) => ((d.getHours() - minH) * 60 + d.getMinutes()) * HOUR_PX / 60;
  // Açılışta ve gün/hafta değişince: bugünü içeriyorsa şu anki saatin biraz üstüne, değilse ilk toplantıya kaydır
  const firstStart = items.length ? Math.min(...items.map(x => x.s.getHours() * 60 + x.s.getMinutes())) : 9 * 60;
  const showsToday = days.some(d => sameDay(d, new Date()));
  useEffect(() => {
    const el = gridRef.current; if (!el) return;
    const t = new Date(); const target = showsToday ? t.getHours() * 60 + t.getMinutes() - 90 : firstStart - 30;
    el.scrollTop = Math.max(0, (target - minH * 60) * HOUR_PX / 60);
  }, [from.getTime(), view]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mcal">
      <div className="mcal__bar">
        <div className="mcal__nav">
          <button type="button" className="btn btn--secondary mcal__icon" onClick={() => step(-1)} aria-label="Önceki"><Icon name="chevronLeft" size={14} /></button>
          <button type="button" className="btn btn--secondary" onClick={() => setAnchor(startOfDay(new Date()))}>Bugün</button>
          <button type="button" className="btn btn--secondary mcal__icon" onClick={() => step(1)} aria-label="Sonraki"><Icon name="chevronRight" size={14} /></button>
          <span className="mcal__label">{label}</span>
        </div>
        <div className="mcal__views">
          {[['week', 'Hafta'], ['day', 'Gün']].map(([k, t]) => (
            <button key={k} type="button" className={`mcal__view ${view === k ? 'is-active' : ''}`} onClick={() => setView(k)}>{t}</button>
          ))}
        </div>
      </div>

      <div className="mcal__grid" ref={gridRef} style={{ '--days': days.length }}>
        <div className="mcal__corner" />
        {days.map(d => (
          <div key={d.toISOString()} className={`mcal__dayhead ${sameDay(d, now) ? 'is-today' : ''}`}
            onClick={() => { if (view === 'week') { setAnchor(d); setView('day'); } }}>
            <span>{d.toLocaleDateString('tr-TR', { weekday: 'short' })}</span><b>{d.getDate()}</b>
          </div>
        ))}
        <div className="mcal__hours">
          {hours.map(h => <div key={h} className="mcal__hour" style={{ height: HOUR_PX }}>{String(h).padStart(2, '0')}:00</div>)}
        </div>
        {days.map(d => {
          const dayItems = layoutDay(items.filter(x => sameDay(x.s, d)));
          return (
            <div key={d.toISOString()} className={`mcal__col ${sameDay(d, now) ? 'is-today' : ''}`} style={{ height: hours.length * HOUR_PX }}>
              {hours.map(h => <div key={h} className="mcal__slot" style={{ height: HOUR_PX }} />)}
              {sameDay(d, now) && now.getHours() >= minH && now.getHours() < maxH && <div className="mcal__now" style={{ top: topOf(now) }} />}
              {dayItems.map(({ m, s, e, col, cols }) => {
                const top = topOf(s); const h = Math.max(22, (e - s) / 60000 * HOUR_PX / 60 - 2);
                const join = joinState(m, now);
                const kind = m.account_status === 'internal' || !m.account_company ? 'internal' : 'brand';
                return (
                  <button key={m.id} type="button" className={`mcal__ev mcal__ev--${kind} ${e < now ? 'is-past' : ''}`}
                    style={{ top, height: h, left: `calc(${(col / cols) * 100}% + 2px)`, width: `calc(${100 / cols}% - 4px)` }}
                    onClick={() => setOpen(m)} title={`${hm(s)} ${m.title}`}>
                    {h < 40 ? (
                      <span className="mcal__ev-line"><b>{hm(s)}</b> {m.title}</span>
                    ) : (<>
                      <span className="mcal__ev-time">{hm(s)}–{hm(e)}{m.fireflies_id ? <Icon name="play" size={9} /> : null}</span>
                      <span className="mcal__ev-title">{m.title}</span>
                    </>)}
                    {h > 44 && m.account_company && <span className="mcal__ev-brand">{m.account_company}</span>}
                    {join === 'now' && (
                      <a className="mcal__join" href={joinUrl(m.meet_link, user?.email)} target="_blank" rel="noopener noreferrer" onClick={(ev) => ev.stopPropagation()}>Katıl</a>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
      {items.length === 0 && <div className="mcal__empty text-muted">Bu aralıkta toplantı yok</div>}
      {open && <MeetingDetail m={open} users={users} onClose={() => setOpen(null)} onPlay={(m) => { setOpen(null); onPlay(m); }} />}
    </div>
  );
}
